import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import './form.css';


const UserDetailsForm = ({ formData, handleChange, nextStep, prevStep }) => {
  const { t } = useTranslation();

  const [errors, setErrors] = useState({});

  const formatCanadianPhone = (numericValue) => {
    // Ensure input is numeric-only
    if (!numericValue) return '';
  
    // Apply formatting based on numeric input length
    if (numericValue.length <= 3) {
      return `(${numericValue}`; // Partial area code with opening bracket
    } else if (numericValue.length <= 6) {
      return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`; // Area code + partial central office code
    } else {
      return `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6, 10)}`; // Full format
    }
  };
  

  const handlePhoneChange = (e) => {
    const rawValue = e.target.value; // Get user input
    const numericValue = rawValue.replace(/\D/g, ''); // Extract numeric characters
  
    // Update formData with unformatted value
    handleChange({
      target: {
        name: 'phone',
        value: numericValue,
      },
    });
  
    // React handles display via the value prop
  };
  

  const validateFields = () => {
    const newErrors = {};

    if (!formData.firstName) {
      newErrors.firstName = t("register_user.first_name_required");
    }

    if (!formData.lastName) {
      newErrors.lastName = t("register_user.last_name_required");
    }

    if (!validatePhone(formData.phone)) {
      newErrors.phone = t("register_user.valid_phone_required");
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = t("register_user.valid_email_required");
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0; // Return true if no errors
  };

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validatePhone = (phone) => /^[0-9]{10}$/.test(phone.replace(/\D/g, '')); // Check 10-digit phone number


  const handleFieldChange = (e) => {
    const { name } = e.target;
    handleChange(e); // Update form data
  
    // Clear error for the field when user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: undefined,
    }));
  };
  
  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    const newErrors = { ...errors };
  
    // Field-specific validation on blur
    if (name === 'firstName' && !value) {
      newErrors.firstName = t("register_user.first_name_required");
    }
  
    if (name === 'lastName' && !value) {
      newErrors.lastName = t("register_user.last_name_required");
    }
  
    if (name === 'phone' && !validatePhone(value)) {
      newErrors.phone = t("register_user.valid_phone_required");
    }
  
    if (name === 'email' && !validateEmail(value)) {
      newErrors.email = t("register_user.valid_email_required");
    }
  
    setErrors(newErrors);
  };
  
  const handlePhoneBlur = (e) => {
    const { value, name } = e.target;
  
    const numericPhone = value.replace(/\D/g, ''); // Remove non-numeric characters
    if (numericPhone.length !== 10) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: t("register_user.valid_phone_required"),
      }));
    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: undefined,
      }));
    }
  };
  // Custom handler for the "Next" button
  const handleNextClick = () => {
    if (validateFields()) {
      nextStep(); 
    }
  };

  return (
    <>
      <form className='form'>
        <h3>{t("register_user.user_details")}</h3>

        <div className='form-row'>
          <label htmlFor='email' className='form-label'>{t("register_user.your_email")} <span className="required">*</span></label>
          <input
            className='form-input'
            type="email"
            name="email"
            value={formData.email}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
          />
          {errors.email && <p className="error-text">{errors.email}</p>}
        </div>

        <div className='form-row'>
          <label htmlFor='firstName' className='form-label'>{t("register_user.first_name")} <span className="required">*</span></label>
          <input
            className='form-input'
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
          />
          {errors.firstName && <p className="error-text">{errors.firstName}</p>}
        </div>

        <div className='form-row'>
          <label htmlFor='lastName' className='form-label'>{t("register_user.last_name")} <span className="required">*</span></label>
          <input
            className='form-input'
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
          />
          {errors.lastName && <p className="error-text">{errors.lastName}</p>}
        </div>

        {/* <div className='form-row'>
          <label htmlFor='phone' className='form-label'>{t("register_user.your_phone_number")} <span className="required">*</span></label>
          <input
            type='text'
            className='form-input'
            id='phone'
            name='phone'
            value={formatCanadianPhone(formData.phone)}
            onChange={handlePhoneChange} 
            onBlur={handlePhoneBlur}
            placeholder='(XXX) XXX-XXXX' 
            maxLength='14' 
          />
          {errors.phone && <p className="error-text">{errors.phone}</p>}
        </div> */}

        <div className='form-row'>
          <label htmlFor='phone' className='form-label'>
            {t("register_user.your_phone_number")} <span className="required">*</span>
          </label>

          <div className="phone-input-container">
            <input
              type="text"
              className="phone-prefix"
              value="+1"
              disabled
            />
            <input
              type='text'
              className='form-input phone-main-input'
              id='phone'
              name='phone'
              value={formatCanadianPhone(formData.phone)}
              onChange={handlePhoneChange}
              onBlur={handlePhoneBlur}
              placeholder='(XXX) XXX-XXXX'
              maxLength='14'
            />
          </div>

          {errors.phone && <p className="error-text">{errors.phone}</p>}
        </div>

        <div className="form-navigation">
          {/* <button type="button" style={{marginRight:'2rem'}} className='btn btn-block btn-secondary' onClick={prevStep}>
          {t("register_user.back")}
          </button> */}
          <button type="button" className='btn btn-block btn-primary'  onClick={handleNextClick}>
          {t("register_user.next")}
          </button>
        </div>
      </form>
    </>
  );
}

export default UserDetailsForm;
